import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { DirectionService } from '../../direction.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth/auth.service';
import { ChatProductSystemKey } from 'src/app/other-pages/chat/chat.service';
import { DialogService } from '../dialog.service';
import { copyTextToClipboard } from 'src/app/utils/clipboard-copy';

@Component({
  selector: 'app-file-issues',
  templateUrl: './file-issues.component.html',
  styleUrls: ['./file-issues.component.css'],
  host: {
    class: 'fill-screen-modal-phone'
  }
})
export class FileIssuesComponent implements OnInit, OnDestroy {
  isRTL: boolean = true;
  private directionSubscription: Subscription;
  isDarkMode: boolean = false;

  fileName: string;
  allowedFormats: string[];
  state: string; // 'file-format-not-supported' | 'file-not-received-in-server' | 'weird-dpi' | 'file-too-large' | 'plotter-processing-failed'
  serverAddress: string;
  fileSizeMb: number | null;
  branchName: string;
  printingService: string;
  is3DPrint: boolean = false;
  fileFormat: string;
  internetSpeed: string;
  internetSpeedValue: number | null;
  dpi: number | null;

  readonly editGuideSoftwareKeys = [
    'q-and-a.2.software.illustrator',
    'q-and-a.2.software.photoshop',
    'q-and-a.2.software.indesign',
    'q-and-a.2.software.autocad',
    'q-and-a.2.software.canva',
    'q-and-a.2.software.powerpoint',
    'q-and-a.2.software.revit',
    'q-and-a.2.software.word',
  ];

  constructor(
    private directionService: DirectionService,
    public dialogRef: MatDialogRef<FileIssuesComponent>,
    private translateService: TranslateService,
    private snackBar: MatSnackBar,
    private router: Router,
    private authService: AuthService,
    private dialogService: DialogService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.fileName = data.fileName;
    this.allowedFormats = data.allowedFormats || [];
    this.state = data.state || 'file-format-not-supported';
    this.serverAddress = data.serverAddress || '';
    this.branchName = data.branchName || '';
    this.printingService = data.printingService || '';
    this.fileFormat = data.fileFormat || '';
    this.is3DPrint = data.is3DPrint || false;
    this.internetSpeed = data.internetSpeed || '';
    this.internetSpeedValue = data.internetSpeedValue || null;
    this.dpi = data.dpi || null;
    this.fileSizeMb = data.fileSizeMb ?? null;
  }

  ngOnInit() {
    this.directionSubscription = this.directionService.direction$.subscribe(direction => {
      this.isRTL = direction === 'rtl';
    });

    this.directionService.isDarkMode$.subscribe(isDarkMode => {
      this.isDarkMode = isDarkMode;
    });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  goToEditInstructions() {
    this.dialogRef.close();
    this.router.navigate(['/edit']);
  }

  openWhatsApp3DPrint() {
    const phoneNumber = this.data.phoneNumber || '97233746962';
    const message = encodeURIComponent('3D-Printing-At-Eazix');
    const url = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(url, '_blank');
  }

  openWhatsAppInstructions() {
    if (this.state === 'file-too-large' || this.state === 'file-format-not-supported' || this.state === 'weird-dpi' || this.state === 'plotter-processing-failed') {
      this.goToEditInstructions();
    } else if (this.state === 'file-not-received-in-server') {
      // בעיית העלאה – פתיחת ווצאפ
      const message = encodeURIComponent('Uploading-Error-At-Eazix');
      const url = `https://wa.me/${this.data.phoneNumber || ''}?text=${message}`;
      window.open(url, '_blank');
    }
  }

  isPhSystemFileFormatIssue(): boolean {
    return this.state === 'file-format-not-supported' && this.printingService === 'ph';
  }

  getChatProductSystem(): ChatProductSystemKey {
    return this.is3DPrint ? '3DPrint' : 'phPrint';
  }

  openSystemChat(): void {
    const uid = this.authService.getUserId() || localStorage.getItem('userId');
    const system = this.getChatProductSystem();
    this.dialogRef.close();
    if (!uid) {
      // שומר כוונה לאחרי login, כמו בכפתורי הצ'אט בבחירת מוצר/סניף.
      this.dialogService.onOpenLoginDialog('', '', system);
      return;
    }
    void this.router.navigate(['/chat', 'user', uid, 'system', system]);
  }

  getSpeedCategory(): string {
    if (this.internetSpeedValue === null) {
      return 'unknown';
    }
    const speed = this.internetSpeedValue;
    if (speed < 1) {
      return 'very-low';
    } else if (speed < 2) {
      return 'low';
    } else if (speed < 4) {
      return 'reasonable';
    } else if (speed < 8) {
      return 'good';
    } else {
      return 'very-good';
    }
  }

  getRoundedSpeed(): string {
    if (this.internetSpeedValue === null) {
      return '0';
    }
    return this.internetSpeedValue.toFixed(1);
  }

  getSpeedMessageKey(): string {
    const category = this.getSpeedCategory();
    return `file-issues.speed-message.${category}`;
  }

  getSpeedLabelKey(): string {
    const category = this.getSpeedCategory();
    return `file-issues.speed-label.${category}`;
  }

  getSpeedMessage(): string {
    const key = this.getSpeedMessageKey();
    return this.translateService.instant(key);
  }

  getSpeedLabel(): string {
    const key = this.getSpeedLabelKey();
    return this.translateService.instant(key);
  }

  copyErrorDetails() {
    const fileFormatDisplay = this.fileFormat && this.fileFormat !== 'N/A' && this.fileFormat !== 'ידוע' 
      ? this.fileFormat.toUpperCase() 
      : 'N/A';
    const errorDetails = [
      `שם קובץ: ${this.fileName}`,
      `סיומת קובץ: ${fileFormatDisplay}`,
      `Endpoint: ${this.serverAddress}`,
      `שם סניף: ${this.branchName}`,
      `שירות הדפסה: ${this.printingService}`,
      `מהירות גלישה: ${this.internetSpeed}`,
      `State: ${this.state}`,
      `פורמטים נתמכים: ${this.allowedFormats.length > 0 ? this.allowedFormats.join(', ').toUpperCase() : 'N/A'}`,
      ...(this.state === 'weird-dpi' && this.dpi ? [`DPI: ${this.dpi}`] : [])
    ].join(' | ');

    void copyTextToClipboard(errorDetails)
      .then(() => {
        this.snackBar.open(this.translateService.instant('file-issues.copy-success'), '', {
          duration: 1000,
          panelClass: ['auto-width', 'center-text'].filter(Boolean),
        });
      })
      .catch((err) => console.error('Could not copy text: ', err));
  }

  ngOnDestroy() {
    if (this.directionSubscription) {
      this.directionSubscription.unsubscribe();
    }
  }
}

